import { useEffect, useMemo, useState } from "react";
import { fetchDataFromLocalStorage, setDataInLocalStorage } from "../utils/helpers";

const readSaved = (storageKey) => {
  const saved = fetchDataFromLocalStorage(storageKey);
  return saved && typeof saved === "object" ? saved : {};
};

export default function useTableColumnSettings({ columns, storageKey, lockedFields = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  // Read straight from storage on the first render so the initial data fetch
  // already knows which columns are hidden.
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = readSaved(storageKey);
    return Array.isArray(saved.order) ? saved.order : null;
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = readSaved(storageKey);
    return saved.visibility && typeof saved.visibility === "object" ? saved.visibility : {};
  });

  useEffect(() => {
    const saved = readSaved(storageKey);
    setColumnOrder(Array.isArray(saved.order) ? saved.order : null);
    setColumnVisibility(
      saved.visibility && typeof saved.visibility === "object" ? saved.visibility : {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;
    const remaining = new Map(columns.map((c) => [c.field, c]));
    const result = [];
    lockedFields.forEach((field) => {
      if (remaining.has(field)) {
        result.push(remaining.get(field));
        remaining.delete(field);
      }
    });
    columnOrder.forEach((field) => {
      if (remaining.has(field)) {
        result.push(remaining.get(field));
        remaining.delete(field);
      }
    });
    columns.forEach((c) => {
      if (remaining.has(c.field)) result.push(c);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, columnOrder]);

  const columnVisibilityModel = useMemo(() => {
    const model = {};
    Object.keys(columnVisibility).forEach((field) => {
      if (columnVisibility[field] === false && !lockedFields.includes(field)) {
        model[field] = false;
      }
    });
    return model;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility]);

  // The columns the grid is showing (locked ones always count as shown). Sent
  // to the list API so only these columns' data comes back.
  const visibleFields = useMemo(
    () =>
      orderedColumns
        .filter((c) => c.field)
        .filter((c) => lockedFields.includes(c.field) || columnVisibility[c.field] !== false)
        .map((c) => c.field),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedColumns, columnVisibility],
  );

  const columnSettingsList = useMemo(
    () =>
      orderedColumns
        .filter((c) => c.field)
        .map((c) => ({ field: c.field, headerName: c.headerName || c.field })),
    [orderedColumns],
  );

  const applySettings = (order, visibility) => {
    setColumnOrder(order);
    setColumnVisibility(visibility);
    setDataInLocalStorage(storageKey, { order, visibility });
    setIsOpen(false);
  };

  const resetSettings = () => {
    setColumnOrder(null);
    setColumnVisibility({});
    setDataInLocalStorage(storageKey, { order: null, visibility: {} });
    setIsOpen(false);
  };

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    orderedColumns,
    columnVisibilityModel,
    columnVisibility,
    visibleFields,
    columnSettingsList,
    applySettings,
    resetSettings,
  };
}
